import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useSessionContext } from "./SessionContext";
import { useI18n } from "../i18n";

/**
 * The GM's tick of a quest objective (GM Studio quests 1) — one place for
 * every view that shows objectives: the quest in Studio and in the table's
 * journal, the quest cards of a beat, «План игры». A tick is a confirmed
 * command (journal:objective-set, a value, not a toggle): the screen shows the
 * server's value; the objective whose tick is on its way is marked pending
 * until the answer — journal:updated with this request id, or command:error
 * (the table's router and Studio already show the refusal). A lost connection
 * forgets the pending ticks: the rejoin snapshot shows what the server kept.
 */
export interface QuestObjectiveTicks {
  readonly set: (entryId: string, objectiveId: string, completed: boolean) => void;
  readonly pending: (entryId: string, objectiveId: string) => boolean;
}

const QuestObjectivesContext = createContext<QuestObjectiveTicks | null>(null);

const keyOf = (entryId: string, objectiveId: string): string => `${entryId}\u0000${objectiveId}`;

export function QuestObjectivesProvider({ children }: { children: ReactNode }) {
  const { mp } = useSessionContext();
  const { t } = useI18n();
  // objective → the request id of the GM's tick awaiting its answer
  const [inFlight, setInFlight] = useState<ReadonlyMap<string, string>>(() => new Map());
  const inFlightRef = useRef(inFlight);
  const mpRef = useRef(mp);
  mpRef.current = mp;
  const campaignId = mp.session?.id;

  const commit = useCallback((next: ReadonlyMap<string, string>): void => {
    inFlightRef.current = next;
    setInFlight(next);
  }, []);

  const set = useCallback(
    (entryId: string, objectiveId: string, completed: boolean): void => {
      const key = keyOf(entryId, objectiveId);
      // One tick of an objective at a time: a second click waits for the answer.
      if (inFlightRef.current.has(key)) return;
      const requestId = crypto.randomUUID();
      const sent = mpRef.current.send({ type: "journal:objective-set", entryId, objectiveId, completed, requestId });
      if (sent === undefined) {
        toast.error(t("session.connectionLost"));
        return;
      }
      commit(new Map(inFlightRef.current).set(key, requestId));
    },
    [t, commit],
  );

  useEffect(() => {
    return mp.onMessage((msg): void => {
      if (msg.type !== "journal:updated" && msg.type !== "command:error") return;
      const requestId = (msg as { requestId?: unknown }).requestId;
      if (typeof requestId !== "string") return;
      for (const [key, id] of inFlightRef.current) {
        if (id !== requestId) continue;
        const next = new Map(inFlightRef.current);
        next.delete(key);
        commit(next);
        return;
      }
    });
  }, [mp, commit]);

  useEffect(() => {
    if (mp.status !== "connected" && inFlightRef.current.size > 0) commit(new Map());
  }, [mp.status, commit]);

  // Another campaign: none of these objectives.
  useEffect(() => {
    return (): void => commit(new Map());
  }, [campaignId, commit]);

  const pending = useCallback(
    (entryId: string, objectiveId: string): boolean => inFlight.has(keyOf(entryId, objectiveId)),
    [inFlight],
  );
  const value = useMemo((): QuestObjectiveTicks => ({ set, pending }), [set, pending]);

  return <QuestObjectivesContext.Provider value={value}>{children}</QuestObjectivesContext.Provider>;
}

/** The tick, or null outside a session (read-only views, tests without a session). */
export function useQuestObjectiveTicks(): QuestObjectiveTicks | null {
  return useContext(QuestObjectivesContext);
}
